import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { SignatureAssetService } from 'src/service/SignatureAssetService';
import { SignatureDialog } from './components/SignatureDialog';
import { confirmDialog } from 'primereact/confirmdialog';

export default function MySignatures() {
    const [signatures, setSignatures] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingSignature, setEditingSignature] = useState<any>(null);

    const loadSignatures = async () => {
        try {
            setLoading(true);
            const data = await SignatureAssetService.getPartySignatureAssets();
            const list = Array.isArray(data) ? data : (data.signatureAssets || data.partySignatureAssets || data.data || []);
            setSignatures(list);
        } catch (error) {
            console.error('Lỗi khi lấy danh sách chữ ký:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSignatures();
    }, []);

    const handleSetDefault = async (id: string) => {
        try {
            await SignatureAssetService.setDefaultSignatureAsset(id);
            loadSignatures();
        } catch (error) {
            console.error('Lỗi khi set mặc định:', error);
        }
    };

    const handleDelete = (id: string) => {
        confirmDialog({
            message: 'Bạn có chắc chắn muốn xóa chữ ký này?',
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await SignatureAssetService.deleteSignatureAsset(id);
                    loadSignatures();
                } catch (error) {
                    console.error('Lỗi khi xóa:', error);
                }
            }
        });
    };
    
    const handleEdit = (sig: any) => {
        setEditingSignature(sig);
        setDialogVisible(true);
    };

    const getImageUrl = (sig: any) => {
        if (sig.contentLocation && sig.contentLocation.startsWith('data:image')) {
            return sig.contentLocation;
        }
        return `/api/rest/s1/iam-signature-assets/resource?signatureAssetId=${sig.signatureAssetId}`;
    };

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-5 border-bottom-1 surface-border pb-4">
                <div>
                    <h2 className="text-2xl font-bold mb-2">Chữ ký của tôi</h2>
                    <p className="text-color-secondary m-0">Quản lý bộ sưu tập chữ ký tay và con dấu cá nhân.</p>
                </div>
                <div className="flex gap-3">
                    <Button 
                        label="Thêm chữ ký/con dấu" 
                        icon="pi pi-plus" 
                        onClick={() => {
                            setEditingSignature(null);
                            setDialogVisible(true);
                        }}
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ height: '200px' }}>
                    <i className="pi pi-spin pi-spinner text-4xl text-primary"></i>
                </div>
            ) : signatures.length === 0 ? (
                <div className="flex flex-column align-items-center justify-content-center mt-6 text-color-secondary opacity-60">
                    <i className="pi pi-folder-open text-6xl mb-4"></i>
                    <span className="text-lg">Chưa có chữ ký nào. Hãy tạo mới ngay!</span>
                </div>
            ) : (
                <div className="grid">
                    {signatures.map((sig, idx) => (
                        <div key={sig.signatureAssetId || idx} className="col-12 md:col-6 lg:col-4 xl:col-3">
                            <div className="card surface-card border-round-xl border-1 surface-border shadow-2 p-0 h-full flex flex-column overflow-hidden transition-all hover:shadow-4">
                                <div className="p-3 bg-surface-50 border-bottom-1 surface-border flex justify-content-between align-items-center">
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-image text-primary" style={{ fontSize: '1.2rem' }}></i>
                                        <span className="font-bold text-sm line-height-1">
                                            {sig.signatureName || 'Chữ ký không tên'}
                                        </span>
                                    </div>
                                    {sig.isDefault === 'Y' && (
                                        <Tag value="Mặc định" severity="success" className="text-xs"></Tag>
                                    )}
                                </div>
                                <div className="flex-1 flex align-items-center justify-content-center p-3" style={{ background: 'var(--surface-ground)', minHeight: '150px' }}>
                                    <img 
                                        src={getImageUrl(sig)} 
                                        alt={sig.signatureName} 
                                        style={{ maxWidth: '100%', maxHeight: '130px', objectFit: 'contain' }}
                                        onError={(e) => { (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1MCIgaGVpZ2h0PSI1MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiNjY2MiIHN0cm9rZS13aWR0aD0iMiI+PHBhdGggZD0iTTIxIDE1djRzMCAwIDAwLS0tLT0iLz48L3N2Zz4='; }}
                                    />
                                </div>
                                <div className="p-3 border-top-1 surface-border flex justify-content-between bg-white mt-auto">
                                    <div className="flex gap-1">
                                        <Button 
                                            icon="pi pi-star-fill" 
                                            text 
                                            severity={sig.isDefault === 'Y' ? 'warning' : 'secondary'}
                                            disabled={sig.isDefault === 'Y'}
                                            onClick={() => handleSetDefault(sig.signatureAssetId)}
                                            tooltip={sig.isDefault === 'Y' ? "Đang là mặc định" : "Đặt làm mặc định"}
                                            tooltipOptions={{ position: 'top' }}
                                        />
                                        <Button 
                                            icon="pi pi-pencil" 
                                            text 
                                            severity="info" 
                                            onClick={() => handleEdit(sig)}
                                            tooltip="Đổi tên/ảnh chữ ký"
                                            tooltipOptions={{ position: 'top' }}
                                        />
                                    </div>
                                    <Button 
                                        icon="pi pi-trash" 
                                        text 
                                        severity="danger" 
                                        onClick={() => handleDelete(sig.signatureAssetId)}
                                        tooltip="Xóa chữ ký"
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <SignatureDialog 
                visible={dialogVisible} 
                onHide={() => {
                    setDialogVisible(false);
                    setEditingSignature(null);
                }} 
                onSave={loadSignatures}
                editSig={editingSignature}
            />
        </div>
    );
}
